import { vaultStore } from '$lib/stores/vaultStore'
import type { IStorage } from './StorageInterface'
import type { Document, DocumentInfo, SearchResult, StorageOptions } from './types'

/**
 * File System based storage using File System Access API
 * Stores documents as .md files in the vault folder
 */
export class FileSystemStorage implements IStorage {
  private options: StorageOptions
  
  constructor(options: StorageOptions = {}) {
    this.options = options
  }
  
  async init(): Promise<void> {
    // Initialize vault if needed
  }
  
  async save(id: string, content: string, metadata?: Partial<Document['metadata']>): Promise<void> {
    // Support paths with folders (e.g., "folder/file" or just "file")
    const filename = id.endsWith('.md') ? id : `${id}.md`
    
    // Check if content already has front matter
    const hasFrontMatter = content.match(/^---[\r\n]/)
    
    let fullContent = content
    
    // Don't automatically add YAML frontmatter for new documents
    // Only update existing frontmatter if it already exists
    if (hasFrontMatter && metadata) {
      // Update existing front matter with new metadata
      fullContent = this.updateFrontMatter(content, metadata)
    }
    // If no frontmatter exists, just save the content as-is
    // This maintains Obsidian compatibility - users can add YAML when needed
    
    await vaultStore.writeFile(filename, fullContent)
  }
  
  async load(id: string, force: boolean = false): Promise<Document> {
    // Support both with and without .md extension
    const filename = id.endsWith('.md') ? id : `${id}.md`
    
    try {
      const content = await vaultStore.readFile(filename, false, force)
      const doc = this.parseDocument(id, content)
      return doc
    } catch (err) {
      console.warn(`Failed to read file ${filename}:`, err)
      
      // In web environment, check if we got content from cache
      if (err instanceof Error && err.message && err.message !== err.name) {
        // If the error message contains actual file content (from cache), use it
        const errorStr = err.message
        if (errorStr.length > 100 && !errorStr.includes('NotFoundError') && !errorStr.includes('not found')) {
          // This might be content from cache incorrectly thrown as error
          try {
            const doc = this.parseDocument(id, errorStr)
            return doc
          } catch (parseErr) {
            // Continue to error handling
          }
        }
      }
      
      // Extract just the filename for the title, not the full path
      const titlePart = id.split('/').pop() || id
      
      // Return a placeholder document for unreadable files
      return {
        id,
        title: `[읽기 실패] ${titlePart}`,
        content: `파일을 읽을 수 없습니다: ${filename}\n\n오류: ${err instanceof Error ? err.message : String(err)}`,
        metadata: {
          wordCount: 0,
          characterCount: 0,
          tags: [],
          lastSaved: new Date(),
          isReadError: true
        },
        createdAt: new Date(),
        updatedAt: new Date()
      }
    }
  }
  
  async delete(id: string): Promise<void> {
    // Check if this is an image file (don't add .md extension)
    const isImage = /\.(png|jpg|jpeg|gif|svg|webp|bmp)$/i.test(id)
    const filename = isImage || id.endsWith('.md') ? id : `${id}.md`
    await vaultStore.deleteFile(filename)

    // If this was an image file, dispatch event to close any open image viewer tabs
    if (isImage && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('image-file-deleted', {
        detail: { filePath: id }
      }))
    }
  }

  /**
   * Rename a document
   * Note: FileSystemStorage reads directly from files, so no in-memory state to update
   * The actual file rename is handled by vaultStore.renameFile() in fileStore
   */
  rename(oldId: string, newId: string, newTitle?: string): void {
    // No-op: FileSystemStorage has no in-memory cache
    // File system changes are handled by vaultStore.renameFile()
    // StorageManager cache is updated separately
  }

  async list(): Promise<DocumentInfo[]> {
    const isElectron = typeof window !== 'undefined' && (window as any).api

    if (isElectron && (window as any).api?.fullScan) {
      // Use MetadataDB cache — no file content reading needed
      const { vaultStore } = await import('$lib/stores/vaultStore')
      const { get } = await import('svelte/store')
      const vault = get(vaultStore)
      const vaultPath = vault?.vaultPath

      if (vaultPath) {
        try {
          const result = await (window as any).api.fullScan(vaultPath)
          if (result.success && result.nodes) {
            const documents: DocumentInfo[] = []
            this.flattenNodesToDocuments(result.nodes, '', documents)
            return documents
          }
        } catch (err) {
          console.error('Failed to load documents from cache, falling back:', err)
        }
      }
    }

    // Fallback: original slow path for web or if cache fails
    return this.listLegacy()
  }

  /**
   * Flatten tree nodes from fullScan into DocumentInfo array.
   * Only includes .md files. Uses metadata only — no file content read.
   */
  private flattenNodesToDocuments(
    nodes: any[],
    parentPath: string,
    documents: DocumentInfo[]
  ): void {
    for (const node of nodes) {
      const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name

      // fullMetadataScan strips .md from name/id, so check extension instead
      if (node.type === 'file' && (node.extension === 'MD' || node.name.endsWith('.md'))) {
        // id: fullScan already strips .md; if not, strip it
        const id = nodePath.replace(/\.md$/, '')
        const titleFromName = node.name.replace(/\.md$/, '')
        const modified = node.modified ? new Date(node.modified) : new Date()

        documents.push({
          id,
          title: titleFromName,
          preview: '',
          createdAt: modified,
          updatedAt: modified,
          wordCount: 0,
          tags: [],
          isReadError: false,
          metadataStatus: 'partial'
        })
      }

      if (node.type === 'folder' && node.children?.length) {
        this.flattenNodesToDocuments(node.children, nodePath, documents)
      }
    }
  }

  /**
   * Legacy list implementation — reads all file contents (slow).
   * Used as fallback for web environment or cache failure.
   */
  private async listLegacy(): Promise<DocumentInfo[]> {
    const files = await vaultStore.listFiles('', true) // recursive: true to get all files
    const documents: DocumentInfo[] = []

    for (const file of files) {
      if (file.type === 'file' && file.name.endsWith('.md')) {
        // Use the full path as the ID for files in folders
        const id = file.path.replace('.md', '')

        try {
          const doc = await this.load(id) // May fail if file doesn't actually exist

          // Use the title as-is
          let title = doc.metadata.title || doc.title

          documents.push({
            id: doc.id,
            title,
            preview: doc.metadata.isReadError ? '[파일을 읽을 수 없습니다]' : doc.content.substring(0, 100),
            createdAt: doc.createdAt,
            updatedAt: doc.updatedAt,
            wordCount: doc.metadata.wordCount,
            tags: doc.metadata.tags,
            isReadError: doc.metadata.isReadError
          })
        } catch (err) {
          // Skip files that can't be loaded (deleted, permission issues, etc.)
          continue
        }
      }
    }

    return documents
  }
  
  async search(query: string): Promise<SearchResult[]> {
    const documents = await this.list()
    const results: SearchResult[] = []
    const lowerQuery = query.toLowerCase()
    
    for (const doc of documents) {
      const titleMatch = doc.title.toLowerCase().includes(lowerQuery)
      const previewMatch = doc.preview.toLowerCase().includes(lowerQuery)
      
      if (titleMatch || previewMatch) {
        results.push({
          documentId: doc.id,
          title: doc.title,
          preview: doc.preview,
          score: titleMatch ? 2 : 1
        })
      }
    }
    
    return results.sort((a, b) => b.score - a.score)
  }
  
  async exists(id: string): Promise<boolean> {
    // For exists check, we need to check all files recursively
    const files = await vaultStore.listFiles('', true)
    const filename = id.endsWith('.md') ? id : `${id}.md`
    return files.some(f => f.path === filename)
  }
  
  async getInfo(id: string): Promise<DocumentInfo | null> {
    try {
      const doc = await this.load(id)
      return {
        id: doc.id,
        title: doc.title,
        preview: doc.content.substring(0, 100),
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
        wordCount: doc.metadata.wordCount,
        tags: doc.metadata.tags
      }
    } catch {
      return null
    }
  }
  
  // Helper methods
  
  private updateFrontMatter(content: string, metadata: Partial<Document['metadata']>): string {
    const frontMatterMatch = content.match(/^(---[\r\n]+[\s\S]*?[\r\n]+---)/)
    
    if (!frontMatterMatch) {
      // No front matter, add new one
      const frontMatter = this.createFrontMatter('', metadata)
      return frontMatter + content
    }
    
    // Parse existing front matter and update it
    const existingFrontMatter = frontMatterMatch[1]
    const lines = existingFrontMatter.split(/[\r\n]+/)
    const updatedLines: string[] = [lines[0]] // Keep the opening ---
    
    // Update or add metadata fields
    const updatedFields = new Set<string>()
    
    for (let i = 1; i < lines.length - 1; i++) {
      const line = lines[i]
      const colonIndex = line.indexOf(':')
      
      if (colonIndex > 0) {
        const key = line.substring(0, colonIndex).trim()
        
        // Update known fields
        if (key === 'updated') {
          updatedLines.push(`updated: ${new Date().toISOString()}`)
          updatedFields.add('updated')
        } else if (key === 'tags' && metadata.tags) {
          updatedLines.push(`tags: ${metadata.tags.join(', ')}`)
          updatedFields.add('tags')
        } else if (key === 'title' && metadata.title) {
          updatedLines.push(`title: ${metadata.title}`)
          updatedFields.add('title')
        } else {
          // Keep existing field
          updatedLines.push(line)
          updatedFields.add(key)
        }
      } else if (line.trim()) {
        updatedLines.push(line)
      }
    }
    
    // Add new fields that weren't in the original
    if (!updatedFields.has('updated')) {
      updatedLines.push(`updated: ${new Date().toISOString()}`)
    }
    
    updatedLines.push(lines[lines.length - 1]) // Keep the closing ---
    
    // Replace old front matter with updated one
    return updatedLines.join('\n') + content.slice(frontMatterMatch[0].length)
  }
  
  private createFrontMatter(id: string, metadata?: Partial<Document['metadata']>): string {
    const now = new Date().toISOString()
    // Use the file id as default title if not provided
    const defaultTitle = id.split('/').pop() || 'untitled'
    const title = metadata?.title || defaultTitle
    const tags = metadata?.tags?.join(', ') || ''
    
    return `---
id: ${id}
title: ${title}
created: ${now}
updated: ${now}
tags: ${tags}
---

`
  }
  
  private parseDocument(id: string, content: string): Document {
    // Parse front matter - handle both --- and --- formats, and no front matter
    const frontMatterMatch = content.match(/^---[\r\n]+([\s\S]*?)[\r\n]+---[\r\n]+/) ||
                            content.match(/^---[\r\n]+([\s\S]*?)[\r\n]+---/) ||
                            content.match(/^---([\s\S]*?)---/)

    let metadata: any = {}

    // Parse front matter for metadata but keep it in content
    if (frontMatterMatch) {
      const frontMatter = frontMatterMatch[1]

      // Parse YAML-like front matter
      frontMatter.split(/[\r\n]+/).forEach(line => {
        const colonIndex = line.indexOf(':')
        if (colonIndex > 0) {
          const key = line.substring(0, colonIndex).trim()
          const value = line.substring(colonIndex + 1).trim()
          if (key && value) {
            metadata[key] = value
          }
        }
      })
    }

    // Use filename as title (physical file name is the source of truth)
    // Get the base name from the id (remove path if present)
    let baseName = id.split('/').pop() || id
    // Remove .md extension if present
    baseName = baseName.replace(/\.md$/, '')
    // Use filename as-is
    const title = baseName
    
    return {
      id,
      title,
      content: content.trim(), // Keep the full content including front matter
      metadata: {
        wordCount: content.length,
        characterCount: content.length,
        tags: metadata.tags ? metadata.tags.split(',').map(t => t.trim()) : [],
        lastSaved: new Date(),
        hasFrontMatter: !!frontMatterMatch
      },
      createdAt: metadata.created ? new Date(metadata.created) : new Date(),
      updatedAt: metadata.updated ? new Date(metadata.updated) : new Date()
    }
  }
}